import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Secuencia para ignorar respuestas obsoletas de /me (evita que una
  // verificación en vuelo sobrescriba una sesión recién iniciada).
  const seqRef = useRef(0);

  const checkAuth = useCallback(async () => {
    const mySeq = ++seqRef.current;
    try {
      const { data } = await api.get("/api/auth/me");
      if (mySeq === seqRef.current) setUser(data);
      return data;
    } catch {
      if (mySeq === seqRef.current) setUser(null);
      return null;
    } finally {
      if (mySeq === seqRef.current) setLoading(false);
    }
  }, []);

  // Establece el usuario autenticado de forma directa (p. ej. tras login con
  // contraseña). Invalida cualquier /me en vuelo para que no lo sobrescriba.
  const setAuthUser = useCallback((u) => {
    seqRef.current += 1;
    setUser(u);
    setLoading(false);
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = (intent) => {
    // Pista de intención (persona/automotora) para preseleccionar el rol en el onboarding.
    // No afecta el mecanismo de autenticación; solo mejora la UX.
    try {
      if (intent) localStorage.setItem("tf_signup_intent", intent);
      else localStorage.removeItem("tf_signup_intent");
    } catch (err) {
      console.debug("no se pudo guardar la intención de registro", err);
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post("/api/auth/logout");
    } catch (err) {
      console.debug("logout request failed (ignored)", err);
    }
    seqRef.current += 1;
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, setAuthUser, loading, login, logout, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}
